import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, Trash2Icon, SendIcon, EyeIcon, RefreshCwIcon, MailIcon, CheckCircleIcon, XIcon, XCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { StatusBadge, ExamStatus } from '../components/ui/StatusBadge';
import { ModalDialog } from '../components/ui/ModalDialog';
import { mockPatients, Patient } from './Patients';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useRole } from '../hooks/useRole';
// Mock exams for the detail view
const mockPatientExams = [{
  id: 'EXM-5001',
  date: '2026-03-11',
  time: '08:00',
  type: 'Risonanza magnetica encefalo',
  status: 'In corso' as ExamStatus,
  doctor: 'Dr. S. Verdi',
  machine: 'Siemens Magnetom'
}, {
  id: 'EXM-4982',
  date: '2025-11-20',
  time: '10:30',
  type: 'TAC torace',
  status: 'Consegnato' as ExamStatus,
  doctor: 'Dr. A. Neri',
  machine: 'GE Revolution'
}, {
  id: 'EXM-4521',
  date: '2025-06-15',
  time: '09:15',
  type: 'Radiografia colonna vertebrale',
  status: 'Refertato' as ExamStatus,
  doctor: 'Dr. M. Gialli',
  machine: 'Carestream DRX'
}];
export function PatientDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const {
    role
  } = useRole();
  const patient = mockPatients.find(p => p.id === id) || mockPatients[0];
  // Auto-activate edit mode if ?edit=true is present
  const startInEditMode = searchParams.get('edit') === 'true';
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [editForm, setEditForm] = useState({
    nome: patient.nome,
    cognome: patient.cognome,
    telefono: patient.telefono,
    email: patient.email,
    indirizzo: patient.indirizzo,
    codiceFiscale: patient.codiceFiscale
  });
  const [savedData, setSavedData] = useState({
    ...editForm
  });
  const [onboardingStatus, setOnboardingStatus] = useState<'Non completato' | 'Completato'>('Non completato');
  const [lastOnboardingDate, setLastOnboardingDate] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({
      type,
      text
    });
    setTimeout(() => setToastMessage(null), 4000);
  };
  const handleEdit = () => {
    setEditForm({
      ...savedData
    });
    setIsEditing(true);
  };
  const handleCancelEdit = () => {
    setEditForm({
      ...savedData
    });
    setIsEditing(false);
    // Clear edit query param if present
    if (searchParams.has('edit')) {
      searchParams.delete('edit');
      setSearchParams(searchParams, {
        replace: true
      });
    }
  };
  const handleConfirmEdit = () => {
    // Check if email changed
    if (editForm.email !== savedData.email) {
      setShowEmailWarning(true);
      return;
    }
    doSaveEdit();
  };
  const doSaveEdit = () => {
    setSavedData({
      ...editForm
    });
    setIsEditing(false);
    if (showEmailWarning) {
      handleOnboarding();
    }
    setShowEmailWarning(false);
    // Clear edit query param if present
    if (searchParams.has('edit')) {
      searchParams.delete('edit');
      setSearchParams(searchParams, {
        replace: true
      });
    }
    showToast('success', 'Anagrafica aggiornata correttamente');
  };
  const handleDelete = () => {
    setShowDeleteModal(true);
  };
  const confirmDelete = () => {
    setShowDeleteModal(false);
    showToast('success', 'Anagrafica eliminata correttamente');
    setTimeout(() => navigate('/patients'), 1500);
  };
  const handleOnboarding = () => {
    const now = new Date();
    setLastOnboardingDate(`${now.toLocaleDateString('it-IT')} ${now.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    })}`);
    showToast('success', 'Richiesta di onboarding inviata correttamente');
  };
  const handleViewReport = (examId: string) => {
    navigate(`/exams/report/${examId}`);
  };
  return <div className="space-y-6 w-full min-w-0 pb-12" data-id="element-1597">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1598">
        <div className="flex items-start gap-3" data-id="element-1599">
          <button onClick={() => navigate('/patients')} className="p-2 hover:bg-clinical-gray rounded-full transition-colors text-text-secondary mt-0.5" data-id="element-1600">
            <ArrowLeftIcon size={20} data-id="element-1601" />
          </button>
          <div data-id="element-1602">
            <h1 className="text-2xl md:text-page-title text-text-primary font-bold" data-id="element-1603">
              {patient.nome} {patient.cognome}
            </h1>
            <p className="text-sm text-text-secondary mt-0.5" data-id="element-1604">
              ID Paziente: {patient.id}
            </p>
          </div>
        </div>
        {/* Desktop action buttons */}
        <div className="hidden md:flex flex-row items-center gap-2" data-id="element-1605">
          {isEditing ? <>
              <button onClick={handleCancelEdit} className="medical-btn-secondary py-2 px-3 text-sm justify-center" data-id="element-1606">
                <XIcon size={16} className="mr-1.5" data-id="element-1607" /> Annulla
              </button>
              <button onClick={handleConfirmEdit} className="medical-btn-primary py-2 px-3 text-sm justify-center" data-id="element-1608">
                <CheckCircleIcon size={16} className="mr-1.5" data-id="element-1609" /> Conferma
                modifiche
              </button>
            </> : <>
              <button onClick={handleEdit} className="medical-btn-secondary py-2 px-3 text-sm justify-center" data-id="element-1610">
                <EditIcon size={16} className="mr-1.5" data-id="element-1611" /> Modifica
              </button>
              <button onClick={handleDelete} className="medical-btn-secondary py-2 px-3 text-sm justify-center text-status-error border-status-error/30 hover:bg-status-error-bg hover:border-status-error" data-id="element-1612">
                <Trash2Icon size={16} className="mr-1.5" data-id="element-1613" /> Elimina
              </button>
            </>}
        </div>
      </div>

      {/* Patient Info Card */}
      <div className="bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 p-5 md:p-6" data-id="element-1614">
        <h3 className="text-table-header text-text-secondary uppercase tracking-wider mb-4" data-id="element-1615">
          Dati Anagrafici
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1616">
          <div data-id="element-1617">
            <span className="text-xs text-text-tertiary block mb-1" data-id="element-1618">Nome</span>
            {isEditing ? <input type="text" value={editForm.nome} onChange={e => setEditForm(prev => ({
            ...prev,
            nome: e.target.value
          }))} className="medical-input text-sm h-9 w-full" data-id="element-1619" /> : <span className="text-sm font-medium text-text-primary" data-id="element-1620">
                {savedData.nome}
              </span>}
          </div>
          <div data-id="element-1621">
            <span className="text-xs text-text-tertiary block mb-1" data-id="element-1622">
              Cognome
            </span>
            {isEditing ? <input type="text" value={editForm.cognome} onChange={e => setEditForm(prev => ({
            ...prev,
            cognome: e.target.value
          }))} className="medical-input text-sm h-9 w-full" data-id="element-1623" /> : <span className="text-sm font-medium text-text-primary" data-id="element-1624">
                {savedData.cognome}
              </span>}
          </div>
          <div data-id="element-1625">
            <span className="text-xs text-text-tertiary block mb-1" data-id="element-1626">
              Codice Fiscale
            </span>
            {isEditing ? <input type="text" value={editForm.codiceFiscale} onChange={e => setEditForm(prev => ({
            ...prev,
            codiceFiscale: e.target.value
          }))} className="medical-input text-sm h-9 w-full" data-id="element-1627" /> : <span className="text-sm font-medium text-text-primary" data-id="element-1628">
                {savedData.codiceFiscale}
              </span>}
          </div>
          <div data-id="element-1629">
            <span className="text-xs text-text-tertiary block mb-1" data-id="element-1630">Email</span>
            {isEditing ? <input type="email" value={editForm.email} onChange={e => setEditForm(prev => ({
            ...prev,
            email: e.target.value
          }))} className="medical-input text-sm h-9 w-full" data-id="element-1631" /> : <div className="flex items-center gap-1.5 text-sm text-text-primary font-medium" data-id="element-1632">
                <MailIcon size={14} className="text-text-tertiary" data-id="element-1633" />
                {savedData.email}
              </div>}
          </div>
          <div data-id="element-1634">
            <span className="text-xs text-text-tertiary block mb-1" data-id="element-1635">
              Telefono
            </span>
            {isEditing ? <input type="text" value={editForm.telefono} onChange={e => setEditForm(prev => ({
            ...prev,
            telefono: e.target.value
          }))} className="medical-input text-sm h-9 w-full" data-id="element-1636" /> : <span className="text-sm font-medium text-text-primary" data-id="element-1637">
                {savedData.telefono}
              </span>}
          </div>
          <div data-id="element-1638">
            <span className="text-xs text-text-tertiary block mb-1" data-id="element-1639">
              Indirizzo di residenza
            </span>
            {isEditing ? <input type="text" value={editForm.indirizzo} onChange={e => setEditForm(prev => ({
            ...prev,
            indirizzo: e.target.value
          }))} className="medical-input text-sm h-9 w-full" data-id="element-1640" /> : <span className="text-sm font-medium text-text-primary" data-id="element-1641">
                {savedData.indirizzo}
              </span>}
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-clinical-gray-100" data-id="element-1642">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1643">
            <div data-id="element-1644">
              <h4 className="text-sm font-semibold text-text-primary mb-1" data-id="element-1645">
                Stato Onboarding
              </h4>
              <div className="flex flex-col gap-1" data-id="element-1646">
                <div className="flex items-center gap-2" data-id="element-1647">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${onboardingStatus === 'Completato' ? 'bg-status-success-bg text-status-success' : 'bg-clinical-gray-200 text-text-secondary'}`} data-id="element-1648">
                    {onboardingStatus === 'Completato' ? 'Onboarding completato (utente ha effettuato primo accesso)' : 'Onboarding non completato'}
                  </span>
                </div>
                {lastOnboardingDate && <span className="text-xs text-text-tertiary" data-id="element-1649">
                    Ultima richiesta inviata: {lastOnboardingDate}
                  </span>}
              </div>
            </div>
            <button onClick={handleOnboarding} className="medical-btn-secondary py-2 px-4 text-sm shrink-0" disabled={isEditing} data-id="element-1650">
              <SendIcon size={16} className="mr-2" data-id="element-1651" /> Invia richiesta onboarding
            </button>
          </div>
        </div>
      </div>

      {/* Exams Section */}
      <div data-id="element-1652">
        <h3 className="text-lg font-semibold text-text-primary mb-4" data-id="element-1653">
          Storico Esami
        </h3>

        {!isMobile ? <div className="w-full max-w-full bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 overflow-hidden" data-id="element-1654">
            <table className="w-full text-left border-collapse" data-id="element-1655">
              <thead data-id="element-1656">
                <tr className="bg-clinical-gray border-b border-clinical-gray-200" data-id="element-1657">
                  <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-1658">
                    Data
                  </th>
                  <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-1659">
                    Prestazione
                  </th>
                  <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-1660">
                    Status
                  </th>
                  <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-1661">
                    Medico
                  </th>
                  <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-1662">
                    Macchinario
                  </th>
                  <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap text-right" data-id="element-1663">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1664">
                {mockPatientExams.map(exam => <tr key={exam.id} className="border-b border-clinical-gray-100 last:border-0 hover:bg-clinical-gray/30 transition-colors" data-id="element-1665">
                    <td className="px-4 py-3 text-sm text-text-primary whitespace-nowrap" data-id="element-1666">
                      {exam.date} {exam.time}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-text-primary" data-id="element-1667">
                      {exam.type}
                    </td>
                    <td className="px-4 py-3" data-id="element-1668">
                      <StatusBadge status={exam.status} data-id="element-1669" />
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary" data-id="element-1670">
                      {exam.doctor}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary" data-id="element-1671">
                      {exam.machine}
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-1672">
                      <div className="flex items-center justify-end gap-2" data-id="element-1673">
                        {(exam.status === 'Refertato' || exam.status === 'Consegnato') && <button onClick={() => handleViewReport(exam.id)} className="medical-btn-secondary py-1 px-2.5 text-xs whitespace-nowrap inline-flex items-center gap-1.5" data-id="element-1674">
                            <EyeIcon size={13} data-id="element-1675" /> Visualizza Referto
                          </button>}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div> : <div className="space-y-3" data-id="element-1676">
            {mockPatientExams.map(exam => <div key={exam.id} className="bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 p-4 space-y-3" data-id="element-1677">
                <div className="flex justify-between items-start" data-id="element-1678">
                  <div className="min-w-0 mr-3" data-id="element-1679">
                    <span className="text-sm font-medium text-text-primary block mb-1" data-id="element-1680">
                      {exam.type}
                    </span>
                    <span className="text-xs text-text-secondary block" data-id="element-1681">
                      {exam.date} {exam.time}
                    </span>
                  </div>
                  <StatusBadge status={exam.status} data-id="element-1682" />
                </div>
                <div className="grid grid-cols-2 gap-2" data-id="element-1683">
                  <div data-id="element-1684">
                    <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-0.5" data-id="element-1685">
                      Medico
                    </span>
                    <span className="text-sm text-text-secondary truncate block" data-id="element-1686">
                      {exam.doctor}
                    </span>
                  </div>
                  <div data-id="element-1687">
                    <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-0.5" data-id="element-1688">
                      Macchinario
                    </span>
                    <span className="text-sm text-text-secondary truncate block" data-id="element-1689">
                      {exam.machine}
                    </span>
                  </div>
                </div>
                <div className="pt-3 border-t border-clinical-gray-100 flex justify-end gap-2" data-id="element-1690">
                  {(exam.status === 'Refertato' || exam.status === 'Consegnato') && <button onClick={() => handleViewReport(exam.id)} className="medical-btn-secondary py-1.5 px-3 text-xs" data-id="element-1691">
                      <EyeIcon size={14} className="mr-1.5" data-id="element-1692" /> Visualizza
                      Referto
                    </button>}
                </div>
              </div>)}
          </div>}
      </div>

      {/* Mobile sticky bottom action buttons */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-clinical-gray-200 px-4 py-3 flex gap-2 z-40 safe-area-bottom pt-[12px] pb-[12px]" data-id="element-1693">
        {isEditing ? <>
            <button onClick={handleCancelEdit} className="medical-btn-secondary py-2.5 px-4 text-sm flex-1 justify-center min-h-[44px]" data-id="element-1694">
              <XIcon size={16} className="mr-1.5" data-id="element-1695" /> Annulla
            </button>
            <button onClick={handleConfirmEdit} className="medical-btn-primary py-2.5 px-4 text-sm flex-1 justify-center min-h-[44px]" data-id="element-1696">
              <CheckCircleIcon size={16} className="mr-1.5" data-id="element-1697" /> Conferma
            </button>
          </> : <>
            <button onClick={handleEdit} className="medical-btn-secondary py-2.5 px-4 text-sm flex-1 justify-center min-h-[44px]" data-id="element-1698">
              <EditIcon size={16} className="mr-1.5" data-id="element-1699" /> Modifica
            </button>
            <button onClick={handleDelete} className="medical-btn-secondary py-2.5 px-4 text-sm flex-1 justify-center text-status-error border-status-error/30 hover:bg-status-error-bg hover:border-status-error min-h-[44px]" data-id="element-1700">
              <Trash2Icon size={16} className="mr-1.5" data-id="element-1701" /> Elimina
            </button>
          </>}
      </div>

      {/* Email Change Warning Modal */}
      <ModalDialog isOpen={showEmailWarning} onClose={() => setShowEmailWarning(false)} title="Modifica indirizzo email" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setShowEmailWarning(false)} data-id="element-1703">
              Annulla
            </button>
            <button className="medical-btn-primary w-full sm:w-auto" onClick={doSaveEdit} data-id="element-1704">
              <CheckCircleIcon size={16} className="mr-1.5" data-id="element-1705" /> Conferma modifica
            </button>
          </>} data-id="element-1702">
        <div className="space-y-4" data-id="element-1706">
          <div className="bg-medical-orange-50 border border-medical-orange/20 rounded-medical-lg p-4 flex items-start gap-3" data-id="element-1707">
            <AlertTriangleIcon className="text-medical-orange shrink-0 mt-0.5" size={20} data-id="element-1708" />
            <div data-id="element-1709">
              <p className="text-sm text-text-primary font-medium mb-1" data-id="element-1710">
                Attenzione: modifica email paziente
              </p>
              <p className="text-sm text-text-secondary" data-id="element-1711">
                Modificando l'indirizzo email del paziente sarà necessario
                inviare una nuova richiesta di onboarding. Vuoi procedere?
              </p>
            </div>
          </div>
          <div className="bg-clinical-gray-50 border border-clinical-gray-200 rounded-medical-lg p-4 space-y-2" data-id="element-1712">
            <div className="flex justify-between items-center" data-id="element-1713">
              <span className="text-sm text-text-secondary" data-id="element-1714">Email attuale</span>
              <span className="text-sm font-medium text-text-primary" data-id="element-1715">
                {savedData.email}
              </span>
            </div>
            <div className="flex justify-between items-center" data-id="element-1716">
              <span className="text-sm text-text-secondary" data-id="element-1717">Nuova email</span>
              <span className="text-sm font-medium text-medical-green" data-id="element-1718">
                {editForm.email}
              </span>
            </div>
          </div>
        </div>
      </ModalDialog>

      {/* Delete Modal */}
      <ModalDialog isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Elimina Anagrafica" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setShowDeleteModal(false)} data-id="element-1720">
              Annulla
            </button>
            <button className="medical-btn-primary w-full sm:w-auto bg-status-error hover:bg-status-error/90 border-status-error" onClick={confirmDelete} data-id="element-1721">
              <Trash2Icon size={16} className="mr-1.5" data-id="element-1722" /> Conferma eliminazione
            </button>
          </>} data-id="element-1719">
        <div className="space-y-4" data-id="element-1723">
          <p className="text-sm text-text-secondary" data-id="element-1724">
            Sei sicuro di voler eliminare l'anagrafica di{' '}
            <span className="font-semibold text-text-primary" data-id="element-1725">
              {patient.nome} {patient.cognome}
            </span>
            ?
          </p>
          <div className="bg-status-error-bg border border-status-error/20 rounded-medical-lg p-4" data-id="element-1726">
            <p className="text-sm text-status-error font-medium" data-id="element-1727">
              Attenzione: questa operazione è irreversibile e rimuoverà
              l'accesso del paziente al portale.
            </p>
          </div>
        </div>
      </ModalDialog>

      {/* Toast Notification */}
      <AnimatePresence data-id="element-1728">
        {toastMessage && <motion.div initial={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} className={`fixed bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-6 sm:bottom-6 z-50 flex items-center gap-3 px-4 py-3 rounded-medical-lg shadow-modal border bg-white w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] sm:w-auto sm:max-w-md ${toastMessage.type === 'success' ? 'border-status-success/20' : 'border-status-error/20'}`} data-id="element-1729">
            {toastMessage.type === 'success' ? <CheckCircleIcon className="text-status-success shrink-0" size={20} data-id="element-1730" /> : <XCircleIcon className="text-status-error shrink-0" size={20} data-id="element-1731" />}
            <span className="text-sm font-medium text-text-primary" data-id="element-1732">
              {toastMessage.text}
            </span>
            <button onClick={() => setToastMessage(null)} className="ml-2 p-1 text-text-tertiary hover:text-text-primary rounded-full transition-colors" data-id="element-1733">
              <XIcon size={16} data-id="element-1734" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}